import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, Shield, ExternalLink } from 'lucide-react';

// Collapsible section component
function Section({ title, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex items-center justify-between text-left hover:bg-slate-50 px-2 -mx-2 rounded"
      >
        <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
        {isOpen ? (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="pb-6 text-slate-600 text-sm leading-relaxed">
          {children}
        </div>
      )}
    </div>
  );
}

// FAQ Item component
function FaqItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-3 flex items-start justify-between text-left"
      >
        <span className="font-medium text-slate-700 pr-4">{question}</span>
        {isOpen ? (
          <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
        )}
      </button>
      {isOpen && (
        <div className="pb-3 text-slate-600 text-sm">{answer}</div>
      )}
    </div>
  );
}

export default function AboutTerms() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b-2 border-black sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="hover:bg-slate-100 p-2 rounded">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold">About & Terms</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Content Guidelines Link */}
        <Link 
          to="/guidelines"
          className="flex items-center justify-between p-4 bg-indigo-50 border-2 border-indigo-200 rounded-xl mb-6 hover:bg-indigo-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-indigo-600" />
            <div>
              <p className="font-semibold text-indigo-800">Content Guidelines</p>
              <p className="text-sm text-indigo-600">What&apos;s allowed and not allowed on LikeKind</p>
            </div>
          </div>
          <ExternalLink className="w-5 h-5 text-indigo-400" />
        </Link>

        <div className="bg-white border-2 border-black rounded-xl p-6 shadow-brutal">
          
          {/* About LikeKind */}
          <Section title="About LikeKind" defaultOpen={true}>
            <p className="mb-4">
              LikeKind is a community platform designed to help people connect through shared interests and local activities.
            </p>
            <p className="mb-4">
              We believe meaningful connections happen when people gather around things they care about — not through algorithms or endless scrolling.
            </p>
            <p>
              Our focus is on real interactions: hosting activities, joining groups, and building relationships within your community.
            </p>
          </Section>

          {/* Terms of Service */}
          <Section title="Terms of Service">
            <p className="mb-4">
              By using LikeKind, you agree to these terms. Please read them carefully.
            </p>
            
            <h4 className="font-semibold text-slate-800 mt-4 mb-2">Platform Rights</h4>
            <p className="mb-4">
              LikeKind is not required to host all content. We reserve the right to remove content that violates our guidelines, harms child safety, or undermines community goodness — regardless of whether it&apos;s technically legal.
            </p>
            
            <h4 className="font-semibold text-slate-800 mt-4 mb-2">Account Responsibility</h4>
            <p className="mb-4">
              You are responsible for maintaining the security of your account and all activity that occurs under it.
            </p>
            
            <h4 className="font-semibold text-slate-800 mt-4 mb-2">Content Standards</h4>
            <p className="mb-4">
              You retain ownership of content you create. By posting, you grant LikeKind a license to display it on the platform. You may not post content that:
            </p>
            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
              <li>Contains nudity or sexualized imagery</li>
              <li>Is harmful to children or families</li>
              <li>Attacks, demeans, or harasses others</li>
              <li>Promotes hatred or violence</li>
              <li>Is deceptive, fraudulent, or spam</li>
            </ul>
            
            <h4 className="font-semibold text-slate-800 mt-4 mb-2">Positivity Standard</h4>
            <p className="mb-4">
              Content must reflect positive intent, respect for others, and community benefit. You may promote yourself — you may not tear others down. This is enforceable and violations may result in content removal or account action.
            </p>
            
            <h4 className="font-semibold text-slate-800 mt-4 mb-2">Activities</h4>
            <p className="mb-4">
              When hosting or attending activities, you accept responsibility for your own safety and conduct. LikeKind facilitates connections but is not responsible for what happens at events.
            </p>
            
            <h4 className="font-semibold text-slate-800 mt-4 mb-2">Termination</h4>
            <p className="mb-4">
              We may suspend or terminate accounts that violate these terms or our community guidelines. Enforcement is transparent — we do not use shadow bans.
            </p>
            
            <p>
              These terms may be updated from time to time. Continued use of the platform constitutes acceptance of any changes.
            </p>
          </Section>

          {/* Community Guidelines Summary */}
          <Section title="Community Guidelines">
            <p className="mb-4">
              LikeKind is built on mutual respect and designed to be safe for children and families.
            </p>
            
            <h4 className="font-semibold text-slate-800 mt-4 mb-2">Zero Tolerance</h4>
            <p className="mb-3">
              We have zero tolerance for:
            </p>
            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
              <li>Sexualized content, nudity, or sexual language</li>
              <li>Content harmful to children</li>
              <li>Harassment, bullying, or targeted attacks</li>
            </ul>
            
            <h4 className="font-semibold text-slate-800 mt-4 mb-2">Positivity Requirement</h4>
            <p className="mb-3">
              Content must be constructive. We do not allow:
            </p>
            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
              <li>Bashing competitors or other businesses</li>
              <li>Rage-bait or fear-based messaging</li>
              <li>Hostile or divisive content</li>
            </ul>
            
            <p className="mb-4">
              If you encounter inappropriate behavior, please report it through the app.
            </p>
            
            <Link to="/guidelines" className="text-indigo-600 hover:underline font-medium">
              Read our full Content Guidelines →
            </Link>
          </Section>

          {/* Sponsorship Terms (LOCKED — NO advertising language) */}
          <Section title="Sponsorship Terms">
            <p className="mb-4">
              LikeKind does not run ads. We support the platform through vetted sponsorship placements only.
            </p>
            
            <h4 className="font-semibold text-slate-800 mt-4 mb-2">Sponsorship Philosophy</h4>
            <p className="mb-4">
              Sponsorship is placement, not advertising. Sponsors fund the platform — they do not influence it. There is no behavioral targeting, no engagement optimization, and no tracking pixels.
            </p>
            
            <h4 className="font-semibold text-slate-800 mt-4 mb-2">What Sponsors Cannot Do</h4>
            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
              <li>Embed external scripts or tracking pixels</li>
              <li>Link to unreviewed external content</li>
              <li>Change content after approval</li>
              <li>Use deceptive thumbnails or bait</li>
              <li>Target minors or restricted audiences</li>
              <li>Access any user data</li>
            </ul>
            
            <h4 className="font-semibold text-slate-800 mt-4 mb-2">What Sponsorship Includes</h4>
            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
              <li>Static, pre-approved content only</li>
              <li>Manual human review before placement</li>
              <li>Clear &quot;Sponsor&quot; labeling on all placements</li>
              <li>&quot;Why am I seeing this?&quot; transparency on every placement</li>
            </ul>
            
            <h4 className="font-semibold text-slate-800 mt-4 mb-2">Sponsor Review & Enforcement</h4>
            <p className="mb-2">
              All sponsor placements are reviewed by humans. Violations result in:
            </p>
            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
              <li><strong>Stage 1:</strong> Immediate removal, payment forfeited</li>
              <li><strong>Stage 2:</strong> Account suspended, 6-month lockout</li>
              <li><strong>Stage 3:</strong> Permanent ban, no refunds</li>
            </ul>
            <p className="text-sm font-semibold" style={{ color: '#dc2626' }}>
              Child safety violations result in immediate permanent ban. No exceptions. No appeals.
            </p>
            
            <Link 
              to="/sponsor-policy" 
              className="inline-block mt-4 text-sm text-indigo-600 hover:underline"
            >
              Read our full Sponsor Policy →
            </Link>
          </Section>

          {/* Moderation & Enforcement */}
          <Section title="Moderation & Enforcement">
            <p className="mb-4">
              LikeKind uses human-in-the-loop moderation. Here&apos;s how it works:
            </p>
            
            <h4 className="font-semibold text-slate-800 mt-4 mb-2">How Decisions Are Made</h4>
            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
              <li>Community members can flag concerning content</li>
              <li>AI assists with detection and prioritization</li>
              <li><strong>All enforcement decisions are made by humans</strong></li>
              <li>We follow written platform rules, not mob opinion</li>
            </ul>
            
            <h4 className="font-semibold text-slate-800 mt-4 mb-2">Transparency</h4>
            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
              <li>We do not use shadow bans or silent penalties</li>
              <li>If action is taken, you will be notified</li>
              <li>Notifications include the reason for action</li>
            </ul>
            
            <h4 className="font-semibold text-slate-800 mt-4 mb-2">Possible Outcomes</h4>
            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
              <li><strong>Approved:</strong> Content reviewed and found acceptable</li>
              <li><strong>Warning:</strong> Content removed, user warned</li>
              <li><strong>Suspension:</strong> Temporary account restriction</li>
              <li><strong>Ban:</strong> Permanent account removal (severe violations)</li>
            </ul>
          </Section>

          {/* Child Safety */}
          <Section title="Child Safety Statement">
            <p className="mb-4">
              LikeKind is designed to be safe for children and families by design — not just by policy.
            </p>
            
            <h4 className="font-semibold text-slate-800 mt-4 mb-2">Our Commitment</h4>
            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
              <li>Zero tolerance for sexualized content of any kind</li>
              <li>No algorithmic amplification of harmful content</li>
              <li>No engagement-bait or conflict-rewarding design</li>
              <li>Human oversight of all moderation decisions</li>
            </ul>
            
            <h4 className="font-semibold text-slate-800 mt-4 mb-2">Structural Safety</h4>
            <p className="mb-4">
              Child safety is built into our systems:
            </p>
            <ul className="list-disc list-inside space-y-1 mb-4 ml-2">
              <li>Content screening before publication</li>
              <li>No sexualized advertising or categories</li>
              <li>No public shaming tools</li>
              <li>Clean, calm user experience by design</li>
            </ul>
            
            <p className="font-medium text-slate-700">
              If safety conflicts with growth, we choose safety. This is non-negotiable.
            </p>
          </Section>

          {/* Privacy */}
          <Section title="Privacy">
            <p className="mb-4">
              LikeKind is designed to work with minimal personal data.
            </p>
            <p className="mb-4">
              We only store information that is necessary for the platform to function.
            </p>
            <p className="mb-4">
              We do not track behavior across the internet, build advertising profiles, or sell personal data.
            </p>
            <p className="mb-4">
              Advertising on LikeKind is contextual and tied to spaces — not people.
            </p>
            <p className="mb-4">
              If information appears publicly, it&apos;s because you chose to make it public as part of using the platform.
            </p>
            <p>
              Nothing else is collected, inferred, or retained beyond what the structure of the product requires.
            </p>
          </Section>

          {/* FAQ */}
          <Section title="FAQ">
            <div className="space-y-1">
              
              {/* Moderation & Content */}
              <h3 className="font-semibold text-slate-800 mt-4 mb-2 text-base">Moderation & Content</h3>
              <FaqItem 
                question="Why was my content rejected or removed?"
                answer={
                  <span>
                    Content may be rejected or removed if it violates our guidelines — including sexual content, negative/hostile language, competitor bashing, or fear-based messaging. You&apos;ll receive a notification explaining the reason and guidance on how to fix it.
                  </span>
                }
              />
              <FaqItem 
                question="Can I criticize competitors?"
                answer={
                  <span>
                    <strong>No.</strong> Our core rule is &quot;Promote yourself — do not tear others down.&quot; You can describe what makes you great without attacking others. Focus on your strengths, not competitors&apos; weaknesses.
                  </span>
                }
              />
              <FaqItem 
                question="Is this platform censored?"
                answer={
                  <span>
                    <strong>No.</strong> We enforce behavior and safety standards, not opinions. You can express viewpoints, share experiences, and disagree respectfully. What we don&apos;t allow is content that harms others, targets children, or degrades the community.
                  </span>
                }
              />
              <FaqItem 
                question="How are moderation decisions made?"
                answer={
                  <span>
                    Community flags increase review priority. AI assists with detection. But all final decisions are made by human moderators following written platform rules. We don&apos;t use shadow bans — if action is taken, you&apos;ll know why.
                  </span>
                }
              />
              
              {/* Safety */}
              <h3 className="font-semibold text-slate-800 mt-6 mb-2 text-base">Safety</h3>
              <FaqItem 
                question="Is this platform safe for children?"
                answer={
                  <span>
                    <strong>Yes.</strong> LikeKind is designed to be safe for children and families by default. We have zero tolerance for sexualized content, and child safety is a design principle — not just a policy.
                  </span>
                }
              />
              <FaqItem 
                question="How do I report inappropriate content?"
                answer="Tap the menu on any profile, post, or message and select 'Report'. Our team reviews all reports and takes action as needed. You'll be notified if we need more information."
              />
              <FaqItem 
                question="What happens when I block someone?"
                answer="They won't be able to see your profile, send you messages, or interact with your content. You also won't see theirs."
              />
              
              {/* Business & Sponsorship */}
              <h3 className="font-semibold text-slate-800 mt-6 mb-2 text-base">Business & Sponsorship</h3>
              <FaqItem 
                question="Does LikeKind have ads?"
                answer="No. LikeKind does not run ads. We support the platform through vetted sponsorship placements only. Sponsors receive zero user data."
              />
              <FaqItem 
                question="How does sponsorship work?"
                answer={
                  <span>
                    Sponsorship is placement, not advertising. Sponsors are manually reviewed, cannot track users, and must follow strict content rules. All placements are clearly labeled &quot;Sponsor&quot; with a &quot;Why am I seeing this?&quot; explanation.
                  </span>
                }
              />
              <FaqItem 
                question="Do sponsors see my data?"
                answer="No. Sponsors receive zero user data. There is no behavioral targeting, no tracking pixels, and no engagement metrics shared."
              />
              
              {/* Getting Started */}
              <h3 className="font-semibold text-slate-800 mt-6 mb-2 text-base">Getting Started</h3>
              <FaqItem 
                question="How do I create an account?"
                answer="Tap 'Sign In' and choose to sign up with your email or Google account. You'll pick a username that others will see when you interact on the platform."
              />
              <FaqItem 
                question="What is a username (codename)?"
                answer="Your username is how you appear to others on LikeKind. It's separate from your real name and gives you control over your public identity."
              />
              <FaqItem 
                question="How do I find activities near me?"
                answer="After setting your location, the home screen shows what's happening nearby. You can browse by category or search for specific interests."
              />
              
            </div>
          </Section>
          
        </div>
        
        {/* Footer */}
        <div className="text-center mt-8 text-sm text-slate-500">
          <p>© {new Date().getFullYear()} LikeKind. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
